import { Injectable } from '@angular/core';
import { ApiHelper } from './apiHelper';
import { ConstantMan } from './constantMan';
import { BehaviorSubject, Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { UserDAO } from './dao/userDAO';
import { User } from 'anchor-shared/lib/model/entity/user';
import { RoleDAO } from './dao/roleDAO';
import { HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { AffiliateStatusService } from './affiliate-status.service';

interface LoginResponse {
  user: User;
  access_token: string;
}

@Injectable()
export class AuthService {
  static PATHS = {
    SEND_MESSAGE: 'sendMessage',
  };

  private userIsAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public loggedInObservable: Observable<boolean> =
    this.userIsAuthenticatedSubject.asObservable();
  userAuthEvent = new BehaviorSubject<User>(null);
  currentUser$ = new BehaviorSubject<User | null | undefined>(undefined);
  private loggedIn = false;

  constructor(
    private apiHelper: ApiHelper,
    private userDAO: UserDAO,
    private roleDAO: RoleDAO,
    private affiliateStatusService: AffiliateStatusService,
  ) {
    this.userIsAuthenticatedSubject.next(false);
  }

  login(username: string, password: string): Subject<boolean> {
    let url = this.apiHelper.getServiceName() + ConstantMan.API.RESOURCE.LOGIN;
    let method = 'POST';
    let httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data = {
      grant_type: 'password',
      username: username,
      password: password,
      scope: `tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    };

    let requestObservable: Observable<LoginResponse> =
      this.apiHelper.apiCall<LoginResponse>(
        url,
        method,
        null,
        data,
        httpHeader,
        null,
      );
    requestObservable.subscribe((reponseData: LoginResponse) => {
      let user = reponseData.user;
      let token = reponseData.access_token;
      this.apiHelper.setAccessToken(token);
      this.userDAO.setCurrentUser(user);
      this.loggedIn = true;
      this.userIsAuthenticatedSubject.next(this.loggedIn);
      this.userAuthEvent.next(user);
      this.currentUser$.next(user);
    });

    return this.userIsAuthenticatedSubject;
  }

  logout(): Observable<any> {
    let url = this.apiHelper.getServiceName() + ConstantMan.API.RESOURCE.LOGOUT;
    let method = 'POST';
    let httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data = {};

    const authSvc = this;

    const requestObservable = this.apiHelper.apiCall(
      url,
      method,
      null,
      data,
      httpHeader,
      null,
    );
    const observable = Observable.create(function subscribe(observer) {
      requestObservable.subscribe(
        (responseData) => {
          authSvc.apiHelper.removeAccessToken();
          authSvc.userDAO.removeCurrentUser();
          authSvc.currentUser$.next(null);
          authSvc.loggedIn = false;
          authSvc.userIsAuthenticatedSubject.next(authSvc.loggedIn);
          observer.next(responseData);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });

    return observable;
  }

  checkSessionCustomer(): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      'customer/' +
      ConstantMan.API.RESOURCE.SESSION;
    const method = 'GET';
    const httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    let sessionObservable = Observable.create((observer) => {
      let requestObservable: Observable<User> = this.apiHelper.apiCall<User>(
        url,
        method,
        null,
        data,
        httpHeader,
        null,
      );
      requestObservable.subscribe(
        (user: User) => {
          this.userDAO.setCurrentUser(user);
          this.currentUser$.next(user);
          observer.next(user);
          if (!this.loggedIn) {
            this.loggedIn = true;
            this.userIsAuthenticatedSubject.next(this.loggedIn);
          }
          observer.complete();
        },
        (err) => {
          this.loggedIn = false;
          this.userIsAuthenticatedSubject.next(this.loggedIn);
        },
      );
    });
    return sessionObservable;
  }

  // Will throw a 401 API error if not authenticated
  checkSession(notifyListeners?: boolean): Observable<any> {
    let url =
      this.apiHelper.getServiceName() + ConstantMan.API.RESOURCE.SESSION;
    let method = 'GET';
    let httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data = {};

    let sessionObservable = Observable.create((observer) => {
      let requestObservable: Observable<User> = this.apiHelper.apiCall<User>(
        url,
        method,
        null,
        data,
        httpHeader,
        null,
      );
      requestObservable.subscribe(
        (user: User) => {
          this.userDAO.setCurrentUser(user);
          this.currentUser$.next(user);
          observer.next(user);
          if (!this.loggedIn || notifyListeners) {
            this.loggedIn = true;
            this.userIsAuthenticatedSubject.next(this.loggedIn);
          }
          observer.complete();
        },
        (err) => {
          this.loggedIn = false;
          this.userIsAuthenticatedSubject.next(this.loggedIn);
        },
      );
    });

    return sessionObservable;
  }

  checkSessionToken(token: string): Observable<any> {
    let url = this.apiHelper.getServiceName() + 'check-session-token';
    let method = 'POST';
    let httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data = {
      sessionToken: token,
    };

    let sessionObservable = Observable.create((observer) => {
      let requestObservable: Observable<User> = this.apiHelper.apiCall<User>(
        url,
        method,
        null,
        data,
        httpHeader,
        null,
      );
      requestObservable.subscribe(
        (auth: any) => {
          this.userDAO.setCurrentUser(auth.user);
          observer.next(auth);
          observer.complete();
        },
        (err) => {
          this.loggedIn = false;
          this.userIsAuthenticatedSubject.next(this.loggedIn);
        },
      );
    });

    return sessionObservable;
  }

  checkSessionPublic(): Observable<any> {
    let url =
      this.apiHelper.getServiceName() + ConstantMan.API.RESOURCE.PUBLIC_SESSION;
    let method = 'GET';
    let httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {};

    let sessionObservable = Observable.create((observer) => {
      let requestObservable: Observable<User> = this.apiHelper.apiCall<User>(
        url,
        method,
        null,
        data,
        httpHeader,
        null,
      );
      requestObservable.subscribe(
        (user: User) => {
          this.currentUser$.next(user);
          this.userDAO.setCurrentUser(user);
          this.loggedIn = !!user;
          this.userIsAuthenticatedSubject.next(this.loggedIn);
          observer.next(user);
          observer.complete();
        },
        (err) => {
          this.currentUser$.next(null);
          this.userDAO.setCurrentUser(null);
          this.loggedIn = false;
          this.userIsAuthenticatedSubject.next(this.loggedIn);
        },
      );
    });

    return sessionObservable;
  }

  sendMessage(subject: string, message: string): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() + AuthService.PATHS.SEND_MESSAGE;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const body = {
      subject: subject,
      message: message,
      key: ConstantMan.TOKENS.SEND_MESSAGE_KEY,
    };

    const httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      body,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (response) => {
          console.log(response);
          observer.next(response);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  sendMessageToRequestsEmail(
    subject: string,
    message: string,
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() + AuthService.PATHS.SEND_MESSAGE;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const body = {
      subject: subject,
      message: message,
      key: ConstantMan.TOKENS.SEND_MESSAGE_TO_REQUESTS_KEY,
    };

    return this.apiHelper.apiCall<any>(url, method, null, body, headers);
  }

  getGoogleAuthUrl(state?: any, flow?: string): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      ConstantMan.API.RESOURCE.GOOGLE_AUTH_URL;
    const method = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    if (flow) {
      headers = headers.append('oauth-flow', flow);
    }
    const data = state;
    return this.apiHelper.apiCall(url, method, null, data, headers);
  }

  getGoogleToken(code: any, flow?: string): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      ConstantMan.API.RESOURCE.GOOGLE_TOKEN +
      `?code=${code}`;
    const method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    if (flow) {
      headers = headers.append('oauth-flow', flow);
    }
    return this.apiHelper.apiCall(url, method, null, null, headers);
  }

  createGoogleUser(tokens: any): Observable<User> {
    const url =
      this.apiHelper.getServiceName() + ConstantMan.API.RESOURCE.GOOGLE_CREATE;
    const method = 'POST';
    const body = {
      tokens: tokens,
    };
    const httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall(url, method, null, body, httpHeader);
  }
  getGoogleUserInfo(tokens: any, flow?: string): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      ConstantMan.API.RESOURCE.GOOGLE_USER_INFO;
    const method = 'POST';
    const body = {
      tokens: tokens,
    };
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    if (flow) {
      headers = headers.append('oauth-flow', flow);
    }
    return this.apiHelper.apiCall(url, method, null, body, headers);
  }

  createBoatOwnerGoogleCalendar(code: any): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      ConstantMan.API.RESOURCE.CREATE_BOAT_OWNER_CAL;
    const method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      flow: 'calendar',
      code,
    };
    return this.apiHelper.apiCall(url, method, null, data, headers);
  }

  syncBoatOwnerGoogleCalendar(vesselId: number): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      ConstantMan.API.RESOURCE.SYNC_BOAT_OWNER_CAL;
    const method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      vesselId: vesselId,
    };
    return this.apiHelper.apiCall(url, method, null, data, headers);
  }

  googleLogin(tokens: any, email: string, flow?: string): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      'google/' +
      ConstantMan.API.RESOURCE.LOGIN;
    const method = 'POST';
    const body = {
      tokens: tokens,
      email: email,
    };
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    if (flow) {
      headers = headers.append('oauth-flow', flow);
    }
    return this.apiHelper.apiCall<User>(url, method, null, body, headers).pipe(
      map((loggedInUser: any) => {
        // should be app access token, not google
        this.apiHelper.setAccessToken(loggedInUser.token);
        this.userDAO.setCurrentUser(loggedInUser.user);
        this.loggedIn = true;
        this.userIsAuthenticatedSubject.next(this.loggedIn);
        return loggedInUser;
      }),
    );
  }

  checkUserSocialStatus(email: string): Observable<any> {
    const url = this.apiHelper.getServiceName() + 'buyflow-user-check';
    const method = 'POST';
    const body = {
      email: email,
    };
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall(url, method, null, body, headers);
  }

  isLoggedIn(): boolean {
    return this.loggedIn;
  }

  getSessionAuthenticatedSubject(): Subject<boolean> {
    return this.userIsAuthenticatedSubject;
  }

  userIsCustomer(user: User): boolean {
    let returnBool = false;
    user.roles.forEach((role) => {
      if (role.type === 'Customer') {
        returnBool = true;
      }
    });
    return returnBool;
  }
}
